import { createSlice } from "@reduxjs/toolkit";
import { playNotifySound } from "../../utils";

import {
  createThread,
  forwardMessageToUsers,
  getMorePrivateMessages,
  getMoreThreadMessages,
  getPrivateChats,
  getPrivateMessages,
  getProjectsWithThreads,
  getThreadMessages,
  getThreads,
  getUnreadCountDm,
  leaveThread,
  updateThread,
} from "../api/conversationAPI";
import {
  getThreadChat,
  starMessageAPI,
  unStarMessageAPI,
  getStarredMessagesAPI,
  getThreadsMembers,
} from "../api/v3/conversationAPI";
import { getOrganizationUsers, getProjectUsers } from "../api/projectAPI";

function resolveActivityMeta(
  text: string,
  attachments?: Array<{ type?: string; name?: string }>
): { activityType: string; preview: string } {
  if (attachments && attachments.length > 0) {
    const first = attachments[0];
    const isImage = first.type?.startsWith("image");
    const isVideo = first.type?.startsWith("video");
    if (isImage) return { activityType: "attachment", preview: "📷 Image" };
    if (isVideo) return { activityType: "attachment", preview: "🎥 Video" };
    return {
      activityType: "attachment",
      preview: first.name ?? "📎 Attachment",
    };
  }
  return { activityType: "message", preview: text };
}

function ensureMaps(state: any) {
  state.threads ??= [];
  state.allChatMessages ??= {};
  state.allChatsUnreadCount ??= {};
  state.allChatsLastMessage ??= {};
  state.allChatsLastActivity ??= {};
  state.allChatsLastActivityType ??= {};
  state.allChatsLastMessageTime ??= {};
  state.allChatsReadStatus ??= {};
  state.allChatsReactorId ??= {};
  state.allChatsLastMessageMeta ??= {};
  state.chatCacheMeta ??= {};
  state.threadMedia ??= {};
  state.pinnedMessages ??= {};
  state.commonThreads ??= {};
}
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  // ── Legacy web state (kept as-is) ─────────────────────────────────────────
  projectsWithThreads: [],
  loading: false,
  getMessageLoading: false,
  error: {},
  status: "idle",
  threadModal: false,
  showThreadProfileDrawer: false,
  threadProfileDrawerCurrenTab: "details",
  newMessageModal: false,
  leaveThread: false,
  deleteThread: false,
  addFolderModal: false,
  addLinkModal: false,
  shareMessageModal: false,
  run: false,
  showTakeTourModal: true,
  deletedMessage: null as {
    chatId: string;
    message: any;
    index: number;
  } | null,
  pinnedMessages: {} as Record<string, any>,
  starredMessages: [] as any[],
  starredMessagesLoading: false,
  shareInMessageModal: {
    type: "folder",
    visible: false,
    url: "",
    file: [],
  },
  viewFileDetails: false,
  threadMedia: {} as Record<string, { media: any[]; documents: any[] }>,
  // Groups-in-common per otherUserId, prefetched by ChatWindow when a private
  // thread opens so UserDetailsDrawer has zero API lag when it's opened.
  commonThreads: {} as Record<string, any[]>,
  deleteFileModal: false,
  deleteFileInMessage: null,
  saveInVaultModal: {
    visible: false,
    file: {},
  },
  shareType: "shareMessage",
  threads: [] as any[],
  selectedUsers: [],
  editThread: false,
  suggestedThread: false,
  threadMembers: [],
  editThreadDetails: {
    threadId: "",
    threadName: "",
    description: "",
    phaseId: "",
    users: ["0"],
    firstname: "",
    lastname: "",
    profilePicture: "",
    profilePictureColorCode: "",
    createdAt: "10-10-9999",
    userToBeAdded: "",
    projectId: "",
    threadProfilePicture: "",
    threadProfilePictureColorCode: "",
  },
  leaveThreadDetails: { threadId: "" },
  deleteThreadDetails: {
    type: "group",
    threadId: "",
    threadName: "",
    description: "",
    phaseId: "",
    users: ["0"],
    firstname: "",
    lastname: "",
    profilePicture: "",
    profilePictureColorCode: "",
    createdAt: "10-10-9999",
    createdById: "",
    email: "",
    userId: "",
    messages: [],
    isNotSelected: true,
    unreadCount: 0,
    limit: 20,
    offset: 0,
    projectId: "",
    isPinned: 0,
    isMute: 0,
  },
  currentThread: {
    type: "group",
    threadId: "",
    threadName: "",
    description: "",
    phaseId: "",
    users: ["0"],
    firstname: "",
    lastname: "",
    profilePicture: "",
    profilePictureColorCode: "",
    createdAt: "10-10-9999",
    createdById: "",
    email: "",
    userId: "",
    messages: [],
    isNotSelected: true,
    unreadCount: 0,
    limit: 20,
    offset: 0,
    projectId: "",
    isPinned: 0,
    isMute: 0,
    tenantId: null,
    threadProfilePicture: "",
    threadProfilePictureColorCode: "",
    senderId: "",
  } as any,
  currentThreadMessage: [] as any[],
  replyMessage: {
    message: "",
    reply: false,
    messageObject: {
      senderName: "",
      attachments: [],
      request: { type: "" },
      _id: "",
    },
  },
  editMessage: {
    edit: false,
    messageObject: { _id: "", message: "" },
  },
  forwardMessage: {},
  deleteMessage: {
    deleteFlag: false,
    deleteMessageObject: { _id: "" },
  },
  chatCacheMeta: {} as Record<
    string,
    {
      source: "server" | "evicted";
      lastServerFetch: number | null;
      messageCount: number;
    }
  >,
  directMessageUsers: [] as any[],
  prefillUserInAddThread: false,
  unreadCount: 0,
  newGroupModal: false,
  requestOption: false,
  userDetailsDrawer: { visible: false, data: {} },
  groupDetailsDrawer: { visible: false, data: {} },
  sharedFilesDrawer: false,
  messageInfoDrawer: { visible: false, message: null },
  sharedFileDetailsDrawer: { visible: false, data: {} },
  chatRequest: {
    selectedRequestType: "Attachment",
    selectedUrgency: "",
    deadlineDate: null,
    deadlineTime: null,
  },
  saveInLibrary: { visible: false, file: {} },
  filePreview: {
    docs: [{ mimeType: "", type: "", name: "", url: "" }],
    visible: false,
    from: "",
    currentIndex: "",
    createdAt: "",
  },
  defaultFileList: [],
  sortedThreads: [],
  drafts: [],
  previousChatId: "",
  quickGlanceChatDraftUpdate: false,
  allChatMessages: {} as Record<string, any[]>,
  allChatsUnreadCount: {} as Record<string, number>,
  allChatsLastMessage: {} as Record<string, string>,
  allChatsLastMessageMeta: {},
  allChatsLastActivity: {} as Record<string, string>,
  allChatsLastActivityType: {} as Record<string, string>,
  allChatsLastMessageTime: {} as Record<string, string>,
  allChatsReadStatus: {} as Record<string, boolean>,
  allChatsReactorId: {} as Record<string, string | null>,
  currentChatId: null as string | null,
  chatPagination: {} as Record<
    string,
    { hasMore: boolean; oldestMessageDate: string | null; loading: boolean }
  >,
};

export const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    addDummyDataForMessagesScreen: (state, action) => {
      const dummyState = action.payload;
      Object.keys(dummyState).forEach(
        (key) => ((state as any)[key] = dummyState[key])
      );
    },
    resetToInitialConversation: (state) => {
      Object.keys(initialState).forEach(
        (key) => ((state as any)[key] = (initialState as any)[key])
      );
    },
    updateConversations: (state, action) => {
      (state as any)[action.payload.key] = action.payload.value;
    },
    updateConversationsMultiple: (state, action) => {
      action.payload.forEach((ele: any) => {
        (state as any)[ele.key] = ele.value;
      });
    },
    resetConversations: (state, action) => {
      action.payload.forEach((ele: string) => {
        (state as any)[ele] = (initialState as any)[ele];
      });
    },
    updateThreadType: (state, action) => {
      state.currentThread.type = action.payload;
    },
    updateMessageContent: (state, action) => {
      ensureMaps(state);
      const { chatId, messageId, message, attachments } = action.payload;
      const chatIdStr = String(chatId);

      const messages = state.allChatMessages[chatIdStr];
      if (messages) {
        const idx = messages.findIndex((m) => m._id === messageId);
        if (idx !== -1) {
          const prev = messages[idx];
          const prevMessage = prev.message ?? prev.content ?? "";
          const contentChanged = prevMessage !== message;

          const updated = [...messages];
          updated[idx] = {
            ...prev,
            message,
            content: message,
            edited: contentChanged ? true : !!prev.edited,
            ...(attachments ? { attachments } : {}),
          };
          state.allChatMessages[chatIdStr] = updated;

          // Keep the sidebar preview in sync if this was the last message in the thread
          const threadIndex = state.threads.findIndex(
            (t) => String(t.threadId) === chatIdStr
          );
          if (
            threadIndex !== -1 &&
            updated[updated.length - 1]._id === messageId
          ) {
            state.threads[threadIndex] = {
              ...state.threads[threadIndex],
              lastMessage: message,
            };
            state.allChatsLastMessage[chatIdStr] = message;
          }
        }
      }

      // Legacy state, kept in sync only in case anything else still reads it
      state.currentThreadMessage = state.currentThreadMessage.map((m) => {
        if (m._id !== messageId) return m;
        const prevMessage = m.message ?? m.content ?? "";
        const contentChanged = prevMessage !== message;
        return {
          ...m,
          message,
          content: message,
          edited: contentChanged ? true : !!m.edited,
          ...(attachments ? { attachments } : {}),
        };
      });
    },
    setPinnedMessage: (state, action) => {
      ensureMaps(state);
      const { chatId, pinnedMessage } = action.payload;
      state.pinnedMessages[String(chatId)] = pinnedMessage;
    },
    setThreadMedia: (state, action) => {
      ensureMaps(state);
      const { chatId, media, documents } = action.payload;
      state.threadMedia[String(chatId)] = {
        media: media ?? [],
        documents: documents ?? [],
      };
    },

    setCommonThreads: (state, action) => {
      ensureMaps(state);
      const { otherUserId, groups } = action.payload;
      if (otherUserId == null) return;
      state.commonThreads[String(otherUserId)] = groups ?? [];
    },
    clearPinnedMessage: (state, action) => {
      ensureMaps(state);
      const { chatId } = action.payload;
      state.pinnedMessages[String(chatId)] = null;
    },
    updatePrivateConversations: (state, action) => {
      const message = action.payload;
      state.directMessageUsers = state.directMessageUsers.map((user) => {
        if (
          +user.userId === +message.senderId &&
          user.messages[user.messages.length - 1]?._id !== message._id
        ) {
          state.unreadCount = 0;
          return {
            ...user,
            messages: [...user.messages, message],
            lastMessage: message.message,
            lastMessageTime: message.createdAt,
            lastMessageLocal: true,
            unreadCount:
              +state.currentThread.userId !== +message.senderId
                ? +user.unreadCount + 1
                : 0,
          };
        }
        return user;
      });
    },
    updateUnreadCountForProjectThreads: (state, action) => {
      const { threadId, projectId } = action.payload;
      if (state.currentThread.threadId != threadId) {
        const project = state.projectsWithThreads.find(
          (p: any) => p.id == projectId
        );
        if (!projectId || !project) {
          state.threads = state.threads.map((t) =>
            +t.threadId === +threadId
              ? { ...t, unreadCount: +t.unreadCount + 1 }
              : t
          );
          return;
        }
        const newThreads = (project as any).threads.map((t: any) =>
          +t.threadId === +threadId
            ? { ...t, unreadCount: +t.unreadCount + 1 }
            : t
        );
        const newProject = { ...(project as any), threads: newThreads };
        state.projectsWithThreads = state.projectsWithThreads.map((p: any) =>
          p.id === projectId ? newProject : p
        );
        playNotifySound();
      }
    },
    // `threadId` covers both groups and DMs now that they share one array.
    // `userId` fallback kept for any caller still using the old DM-only shape.
    updateUnreadCountForMarkAsUnread: (state, action) => {
      const { unreadCount, threadId, userId } = action.payload;
      if (threadId) {
        state.threads = state.threads.map((thread) =>
          thread.threadId == threadId ? { ...thread, unreadCount } : thread
        );
      } else {
        state.threads = state.threads.map((thread) =>
          thread.userId === userId || thread.userid === userId
            ? { ...thread, unreadCount }
            : thread
        );
      }
      state.unreadCount = unreadCount;
    },
    updateReaction: (state, action) => {
      ensureMaps(state);
      const { messageId, emoji, userId, threadId } = action.payload;
      if (threadId == null) return;
      const chatIdStr = String(threadId);

      const messages = state.allChatMessages[chatIdStr];
      if (!messages) return;

      const idx = messages.findIndex((m) => m._id === messageId);
      if (idx === -1) return;

      const msg = messages[idx];
      const reactions = msg.reactions || {};

      // A user can only have ONE reaction on a message at a time — mirror
      // the backend: strip this user out of every emoji bucket first, then
      // decide whether to re-add them to `emoji` (toggle off) or add them
      // (switch/new reaction).
      const wasOnThisEmoji = (reactions[emoji] || []).includes(userId);

      const strippedReactions = Object.fromEntries(
        Object.entries(reactions).map(([e, userIds]) => [
          e,
          (userIds as any[]).filter((u) => u != userId),
        ])
      );

      const nextReactions = { ...strippedReactions };
      if (!wasOnThisEmoji) {
        // switching to (or newly adding) this emoji
        nextReactions[emoji] = [...(strippedReactions[emoji] || []), userId];
      }
      // if wasOnThisEmoji, leave it stripped → acts as "unreact" (toggle off)

      // drop empty emoji keys so the UI doesn't render 0-count pills
      const cleaned = Object.fromEntries(
        Object.entries(nextReactions).filter(
          ([, ids]) => (ids as any[]).length > 0
        )
      );

      const updated = [...messages];
      updated[idx] = { ...msg, reactions: cleaned };
      state.allChatMessages[chatIdStr] = updated;
    },
    starMessageLocally: (state, action) => {
      ensureMaps(state);
      const { chatId, messageId, userId, starring } = action.payload;
      const chatIdStr = String(chatId);
      const messages = state.allChatMessages[chatIdStr];
      if (!messages) return;

      const idx = messages.findIndex((m) => m._id === messageId);
      if (idx === -1) return;

      const msg = messages[idx];
      const current: string[] = Array.isArray(msg.starred) ? msg.starred : [];
      const updatedStarred = starring
        ? Array.from(new Set([...current, String(userId)]))
        : current.filter((id) => id !== String(userId));

      const updated = [...messages];
      updated[idx] = { ...msg, starred: updatedStarred };
      state.allChatMessages[chatIdStr] = updated;
    },
    removeStarredMessage: (state, action) => {
      state.starredMessages = state.starredMessages.filter(
        (m) => m._id !== action.payload
      );
    },
    clearStarredMessages: (state) => {
      state.starredMessages = [];
    },
    muteThread: (state, action) => {
      const { threadId, projectId, isProjectThread, isMute } = action.payload;
      if (isProjectThread) {
        state.projectsWithThreads = state.projectsWithThreads.map(
          (project: any) => {
            if (project.threads.length > 0 && +project.id === +projectId) {
              const threads = project.threads.map((thread: any) =>
                +thread.threadId === +threadId ? { ...thread, isMute } : thread
              );
              return { ...project, threads };
            }
            return project;
          }
        );
      } else {
        state.threads = state.threads.map((thread) =>
          +thread.threadId === +threadId ? { ...thread, isMute } : thread
        );
      }
    },
    // Works for both groups (isThread → threadId match) and DMs (userId
    // match) since both now live in `state.threads`.
    updateThreadForUser: (state, action) => {
      const { threadId, isThread, userId, isMute, isPinned } = action.payload;
      const updateObj: { isMute?: number; isPinned?: number } = {};
      if (typeof isMute !== "undefined") updateObj.isMute = isMute;
      if (typeof isPinned !== "undefined") updateObj.isPinned = isPinned;

      if (isThread) {
        state.threads = state.threads.map((thread) =>
          +thread.threadId === +threadId ? { ...thread, ...updateObj } : thread
        );
        if (state.currentThread.threadId === threadId) {
          state.currentThread = { ...state.currentThread, ...updateObj };
        }
      } else {
        state.threads = state.threads.map((thread) =>
          +thread.userId === +userId || +thread.userid === +userId
            ? { ...thread, ...updateObj }
            : thread
        );
        if (state.currentThread.userId === userId) {
          state.currentThread = { ...state.currentThread, ...updateObj };
        }
      }
    },
    updateThreadMessage: (state, action) => {
      if (
        +state.currentThread.threadId === +action.payload.threadId &&
        state.currentThreadMessage[state.currentThreadMessage.length - 1]
          ?._id !== action.payload._id
      ) {
        state.unreadCount = 0;
        state.currentThreadMessage = [
          ...state.currentThreadMessage,
          action.payload,
        ];
      }

      const projectId = action.payload.projectId;
      if (projectId) {
        state.projectsWithThreads = state.projectsWithThreads.map(
          (project: any) => {
            if (state.currentThread.projectId == project.id) {
              const newThreads = project.threads
                ? project.threads.map((thread: any) => {
                    if (thread.threadId == state.currentThread.threadId) {
                      return {
                        ...thread,
                        messages: [
                          ...state.currentThreadMessage,
                          action.payload,
                        ],
                      };
                    }
                    return thread;
                  })
                : [];
              return { ...project, threads: newThreads };
            }
            return project;
          }
        );
      } else {
        state.threads = state.threads.map((thread) => {
          if (thread.threadId == state.currentThread.threadId) {
            return {
              ...thread,
              messages: [...state.currentThreadMessage, action.payload],
              lastMessage: `${action.payload.senderName}: ${action.payload.message}`,
              lastMessageTime: action.payload.createdAt,
              lastMessageLocal: true,
            };
          }
          return thread;
        });
      }
    },
    replyToRequest: (state, action) => {
      const { message, parentMessageId } = action.payload;
      if (
        message.threadId &&
        +state.currentThread.threadId === +message.threadId
      ) {
        state.currentThreadMessage = state.currentThreadMessage.map((m) => {
          if (m._id === parentMessageId) {
            return {
              ...m,
              request: {
                ...m.request,
                updateCount: m.request.updateCount + 1,
                activity: [
                  ...m.request.activity,
                  { ...message, replyMessage: {}, request: {} },
                ],
              },
            };
          }
          return m;
        });
      } else {
        state.threads = state.threads.map((thread) => {
          if (
            +thread.userId === +state.currentThread.userId ||
            +thread.userid === +state.currentThread.userId
          ) {
            return {
              ...thread,
              messages: (thread.messages || []).map((m: any) => {
                if (m._id === parentMessageId) {
                  return {
                    ...m,
                    request: {
                      ...m.request,
                      updateCount: m.request.updateCount + 1,
                      activity: [
                        ...m.request.activity,
                        { ...message, replyMessage: {}, request: {} },
                      ],
                    },
                  };
                }
                return m;
              }),
            };
          }
          return thread;
        });
      }
    },
    closeRequest: (state, action) => {
      const { message } = action.payload;
      if (
        message.threadId &&
        +state.currentThread.threadId === +message.threadId
      ) {
        state.currentThreadMessage = state.currentThreadMessage.map((m) =>
          m._id === message._id
            ? {
                ...m,
                request: {
                  ...m.request,
                  status: "Closed",
                  requestClosingDate: new Date().toISOString(),
                },
              }
            : m
        );
      } else {
        state.threads = state.threads.map((thread) => {
          if (
            +thread.userId === +state.currentThread.userId ||
            +thread.userid === +state.currentThread.userId
          ) {
            return {
              ...thread,
              messages: (thread.messages || []).map((m: any) =>
                m._id === message._id
                  ? {
                      ...m,
                      request: {
                        ...m.request,
                        status: "Closed",
                        requestClosingDate: new Date().toISOString(),
                      },
                    }
                  : m
              ),
            };
          }
          return thread;
        });
      }
    },

    // ── New: message cache ───────────────────────────────────────────────────

    /**
     * addMessage
     *
     * Adds an incoming (or locally-optimistic) message to allChatMessages[chatId].
     * Also:
     *   • Bubbles the thread to position 0 in threads[] — works for DMs and
     *     groups alike since both live in the same array now.
     *   • Updates allChatsLastMessageTime
     *   • Updates allChatsLastActivity / allChatsLastActivityType
     *
     * For messages arriving in a chat the user has never opened (no cache yet)
     * we skip writing to allChatMessages to avoid phantom arrays, but we still
     * update every preview/sort map so the sidebar row stays accurate.
     *
     * IMPORTANT: `lastMessage` (what ChatCard actually renders, via
     * `thread.lastMessage || thread.lastActivity`) must carry the same
     * resolved label ("🎥 Video", "📷 Image", etc.) that resolveActivityMeta
     * produces — not a generic "Attachment" placeholder. `thread.lastMessage`
     * is always a truthy string once set, so ChatCard's `||` fallback to
     * `lastActivity` never kicks in; if lastMessage says "Attachment" that's
     * what shows, permanently, until the thread is reloaded from the server
     * (which sends its own correct preview string).
     */
    addMessage: (state, action) => {
      ensureMaps(state);
      const { chatId, message, local } = action.payload;
      const chatIdStr = String(chatId);

      if (local === true) {
        message.local = true;
      }

      const { activityType, preview } = resolveActivityMeta(
        message.content || message.message || "",
        message.attachments
      );

      const existingMessages = state.allChatMessages[chatIdStr];
      const isCurrentChat = String(state.currentChatId) === chatIdStr;

      if (
        !local &&
        !isCurrentChat &&
        (!existingMessages || existingMessages.length === 0)
      ) {
        const threadIndex = state.threads.findIndex(
          (t) => String(t.threadId) === chatIdStr
        );
        if (threadIndex !== -1) {
          state.threads[threadIndex] = {
            ...state.threads[threadIndex],
            lastMessage: message.content || message.message || preview,
            lastMessageUpdate: message.createdAt || new Date().toISOString(),
          };
        }

        state.allChatsLastMessageTime[chatIdStr] =
          message.createdAt || new Date().toISOString();

        state.allChatsLastActivity[chatIdStr] = preview;
        state.allChatsLastActivityType[chatIdStr] = activityType;
        return;
      }

      // Normal path — write to cache
      const messages = existingMessages || [];

      if (local === true) {
        // Replace the trailing local message (optimistic) rather than stacking
        const index = messages.findIndex((msg) => msg.local);
        if (index !== -1) {
          const updated = [...messages];
          updated.pop();
          updated.push(message);
          state.allChatMessages[chatIdStr] = updated;
        } else {
          state.allChatMessages[chatIdStr] = [...messages, message];
        }
      } else {
        const existingIndex = messages.findIndex(
          (msg) => msg._id === message._id
        );
        if (existingIndex !== -1) {
          // Patch in-place (optimistic upload → real URL swap)
          const updated = [...messages];
          updated[existingIndex] = message;
          state.allChatMessages[chatIdStr] = updated;
        } else {
          state.allChatMessages[chatIdStr] = [...messages, message];
        }
      }

      // Sidebar sort maps
      state.allChatsLastMessageTime[chatIdStr] =
        message.createdAt || new Date().toISOString();

      const threadIndex = state.threads.findIndex(
        (t) => String(t.threadId) === chatIdStr
      );
      if (threadIndex !== -1) {
        const updatedThread = {
          ...state.threads[threadIndex],
          lastMessage: message.content || message.message || preview,
          lastMessageUpdate: message.createdAt || new Date().toISOString(),
        };
        const newThreads = [...state.threads];
        newThreads.splice(threadIndex, 1);
        newThreads.unshift(updatedThread);
        state.threads = newThreads;
      }

      state.allChatsLastActivity[chatIdStr] = preview;
      state.allChatsLastActivityType[chatIdStr] = activityType;
    },
    deleteLocalMessage: (state, action) => {
      ensureMaps(state);
      const { chatId, message, lastMessageTime } = action.payload;
      const chatIdStr = String(chatId);
      const messages = state.allChatMessages[chatIdStr];

      const deletedMessageIndex =
        messages?.findIndex((m) => m._id === message._id) ?? -1;
      state.deletedMessage = {
        chatId: chatIdStr,
        message,
        index: deletedMessageIndex,
      };

      if (!messages) return;

      const filteredMessages = messages.filter((m) => m._id !== message._id);
      state.allChatMessages[chatIdStr] = filteredMessages;

      const wasLastMessage =
        deletedMessageIndex !== -1 &&
        deletedMessageIndex === messages.length - 1;

      if (wasLastMessage) {
        const newLast = filteredMessages[filteredMessages.length - 1];
        const { activityType: newType, preview: newPreview } = newLast
          ? resolveActivityMeta(
              newLast.content || newLast.message || "",
              newLast.attachments
            )
          : { activityType: "message", preview: "" };

        state.allChatsLastMessage[chatIdStr] = newPreview;
        state.allChatsLastActivity[chatIdStr] = newPreview;
        state.allChatsLastActivityType[chatIdStr] = newType;
        state.allChatsLastMessageTime[chatIdStr] =
          lastMessageTime ?? newLast?.createdAt ?? null;

        const threadIndex = state.threads.findIndex(
          (t) => String(t.threadId) === chatIdStr
        );
        if (threadIndex !== -1) {
          state.threads[threadIndex] = {
            ...state.threads[threadIndex],
            lastMessage: newPreview,
            lastMessageUpdate:
              newLast?.createdAt ??
              state.threads[threadIndex].lastMessageUpdate,
          };
        }
      }

      if (state.pinnedMessages?.[chatIdStr]?._id === message._id) {
        state.pinnedMessages[chatIdStr] = null;
      }
    },

    removeAttachmentFromMessage: (state, action) => {
      ensureMaps(state);
      const { chatId, messageId, attachmentUrl, remainingAttachments } =
        action.payload;
      const chatIdStr = String(chatId);

      const messages = state.allChatMessages[chatIdStr];
      if (!messages) return;

      const msgIndex = messages.findIndex((m) => m._id === messageId);
      if (msgIndex === -1) return;

      const msg = messages[msgIndex];
      const hasText = !!(msg.content || msg.message);
      const shouldDeleteWhole = remainingAttachments.length === 0 && !hasText;

      if (shouldDeleteWhole) {
        const filteredMessages = messages.filter((m) => m._id !== messageId);
        state.allChatMessages[chatIdStr] = filteredMessages;

        if (msgIndex === messages.length - 1) {
          const newLast = filteredMessages[filteredMessages.length - 1];
          const { activityType: newType, preview: newPreview } = newLast
            ? resolveActivityMeta(
                newLast.content || newLast.message || "",
                newLast.attachments
              )
            : { activityType: "message", preview: "" };

          state.allChatsLastMessage[chatIdStr] = newPreview;
          state.allChatsLastActivity[chatIdStr] = newPreview;
          state.allChatsLastActivityType[chatIdStr] = newType;

          const threadIndex = state.threads.findIndex(
            (t) => String(t.threadId) === chatIdStr
          );
          if (threadIndex !== -1) {
            state.threads[threadIndex] = {
              ...state.threads[threadIndex],
              lastMessage: newPreview,
              lastMessageUpdate:
                newLast?.createdAt ??
                state.threads[threadIndex].lastMessageUpdate,
            };
          }
        }

        if (state.pinnedMessages?.[chatIdStr]?._id === messageId) {
          state.pinnedMessages[chatIdStr] = null;
        }
      } else {
        state.allChatMessages[chatIdStr][msgIndex] = {
          ...msg,
          attachments: remainingAttachments,
        };

        if (msgIndex === messages.length - 1) {
          const { activityType: newType, preview: newPreview } =
            resolveActivityMeta(
              msg.content || msg.message || "",
              remainingAttachments
            );
          state.allChatsLastMessage[chatIdStr] = newPreview;
          state.allChatsLastActivity[chatIdStr] = newPreview;
          state.allChatsLastActivityType[chatIdStr] = newType;
        }
      }
    },

    evictChatMessages: (state, action) => {
      ensureMaps(state);
      const { chatId } = action.payload;
      const chatIdStr = String(chatId);
      const messages = state.allChatMessages[chatIdStr];
      if (messages && messages.length > 60) {
        const trimmed = messages.slice(-60);
        state.allChatMessages[chatIdStr] = trimmed;
        state.chatCacheMeta[chatIdStr] = {
          source: "evicted",
          lastServerFetch:
            state.chatCacheMeta[chatIdStr]?.lastServerFetch ?? null,
          messageCount: trimmed.length,
        };
        state.chatPagination[chatIdStr] = {
          hasMore: true,
          oldestMessageDate: trimmed[0]?.createdAt ?? null,
          loading: false,
        };
      }
    },

    // ── New: unread count ────────────────────────────────────────────────────

    /**
     * updateAllChatUnreadCount
     *
     * { chatId, value, reset?, isPrivate?, message? }
     *
     * reset=true  → set count to `value` (used when opening a chat / server sync)
     * reset=false → increment by `value` (used on each incoming message)
     *
     * `threads[]` holds both DMs and groups now, so a single lookup by
     * threadId keeps the sidebar badge in sync regardless of chat type.
     */
    updateAllChatUnreadCount: (state, action) => {
      ensureMaps(state);
      const { chatId, value, reset } = action.payload;
      const key = String(chatId);

      if (reset === true) {
        const newCount = Math.max(0, value);
        state.allChatsUnreadCount[key] = newCount;

        // Keep threads[].unreadCount in sync so badge renders correctly
        const threadIdx = state.threads.findIndex(
          (t) => String(t.threadId) === key
        );
        if (threadIdx !== -1) {
          state.threads[threadIdx] = {
            ...state.threads[threadIdx],
            unreadCount: newCount,
          };
        }
      } else {
        const next = (state.allChatsUnreadCount[key] || 0) + value;
        state.allChatsUnreadCount[key] = Math.max(0, next);

        const threadIdx = state.threads.findIndex(
          (t) => String(t.threadId) === key
        );
        if (threadIdx !== -1) {
          state.threads[threadIdx] = {
            ...state.threads[threadIdx],
            unreadCount: state.allChatsUnreadCount[key],
          };
        }
      }
    },

    // ── New: sidebar preview helpers ─────────────────────────────────────────

    updateAllChatLastMessage: (state, action) => {
      ensureMaps(state);
      const { chatId, value } = action.payload;
      state.allChatsLastMessage[String(chatId)] = value;
    },

    /**
     * updateLastMessageTime
     *
     * Updates allChatsLastMessageTime and re-sorts threads[] so the most-recently
     * active chat floats to the top (pinned chats always stay above unpinned ones).
     * Sorts the single unified array — DMs and groups interleave by recency,
     * same as any real chat app sidebar.
     */
    updateLastMessageTime: (state, action) => {
      ensureMaps(state);
      const { threadId, createdAt } = action.payload;

      state.threads = state.threads.map((thread) =>
        +thread.threadId === +threadId
          ? { ...thread, lastMessageUpdate: createdAt }
          : thread
      );

      state.threads.sort((a, b) => {
        if ((a.isPinned ?? 0) !== (b.isPinned ?? 0))
          return (b.isPinned ?? 0) - (a.isPinned ?? 0);
        if ((a.isPinned ?? 0) === 1 && (b.isPinned ?? 0) === 1)
          return (
            new Date(b.pinnedAt ?? 0).getTime() -
            new Date(a.pinnedAt ?? 0).getTime()
          );
        return (
          new Date(b.lastMessageUpdate ?? 0).getTime() -
          new Date(a.lastMessageUpdate ?? 0).getTime()
        );
      });
      state.allChatsLastMessageTime[String(threadId)] = createdAt;
    },

    updateAllChatReadStatus: (state, action) => {
      ensureMaps(state);
      const { chatId, value } = action.payload;
      state.allChatsReadStatus[String(chatId)] = value;
    },

    updateThreadLastActivity: (state, action) => {
      ensureMaps(state);
      const { threadId, lastActivity, lastActivityType, reactorId } =
        action.payload;
      const key = String(threadId);
      state.allChatsLastActivity[key] = lastActivity;
      state.allChatsLastActivityType[key] = lastActivityType ?? "message";
      state.allChatsReactorId[key] = reactorId ?? null;
    },

    // ── New: delivery / read ticks ───────────────────────────────────────────

    /**
     * updateMessageReceived  — single-tick → double-tick (grey)
     * Marks one message as delivered by appending userId to messageReceivedBy[].
     */
    updateMessageReceived: (state, action) => {
      ensureMaps(state);
      const { chatId, message, userId } = action.payload;
      const chatIdStr = String(chatId);
      const msg = state.allChatMessages[chatIdStr]?.find(
        (m) => m._id === message._id
      );
      if (!msg) return;
      if (
        (msg.messageReceivedBy ?? []).some(
          (r: any) => String(r?.userId) === String(userId)
        )
      )
        return;
      if (!msg.messageReceivedBy) msg.messageReceivedBy = [];
      msg.messageReceivedBy.push({
        userId: String(userId),
        timestamp: new Date().toISOString(),
      });
    },

    /**
     * updateMessageRead  — double-tick blue
     * Marks ALL messages in the chat as read by the given userId.
     */
    updateMessageRead: (state, action) => {
      ensureMaps(state);
      const { chatId, userId, user } = action.payload;
      const chatIdStr = String(chatId);
      if (!state.allChatMessages[chatIdStr]) return;
      for (const msg of state.allChatMessages[chatIdStr]) {
        if ((msg.messageReadBy ?? []).some((r: any) => r.userId == userId))
          continue;
        if (!msg.messageReadBy) msg.messageReadBy = [];
        msg.messageReadBy.push({
          user,
          userId,
          timestamp: new Date().toISOString(),
        });
      }
    },
    // Both `groupProfilePicture` and `profilePicture` are kept in sync since
    // different screens read different field names off the same thread
    // object (ChatHeader reads groupProfilePicture, some cards read
    // profilePicture). `profilePicture` in the payload can be an empty
    // string ("") to explicitly clear the photo — only `undefined` means
    // "leave it as-is".
    updateGroupInfo: (state, action) => {
      const { groupId, name, profilePicture } = action.payload;
      const key = String(groupId);

      const applyUpdate = (thread: any) => {
        const next = { ...thread };
        if (typeof name !== "undefined") {
          next.name = name;
          next.threadName = name;
        }
        if (typeof profilePicture !== "undefined") {
          next.groupProfilePicture = profilePicture;
          next.profilePicture = profilePicture;
        }
        return next;
      };

      state.threads = state.threads.map((thread) =>
        String(thread.threadId) === key ? applyUpdate(thread) : thread
      );

      if (String(state.currentThread.threadId) === key) {
        state.currentThread = applyUpdate(state.currentThread);
      }
    },
    groupDeletedAction: (state, action) => {
      ensureMaps(state);
      const { groupId } = action.payload;
      const key = String(groupId);

      state.threads = state.threads.filter((t) => String(t.threadId) !== key);

      delete state.allChatMessages[key];
      delete state.allChatsUnreadCount[key];
      delete state.allChatsLastMessage[key];
      delete state.allChatsLastActivity[key];
      delete state.allChatsLastActivityType[key];
      delete state.allChatsLastMessageTime[key];
      delete state.allChatsReadStatus[key];
      delete state.allChatsReactorId[key];
      delete state.allChatsLastMessageMeta[key];
      delete state.chatCacheMeta[key];
      delete state.chatPagination[key];
      delete state.pinnedMessages[key];

      if (String(state.currentThread.threadId) === key) {
        state.currentThread = initialState.currentThread;
        state.currentThreadMessage = [];
      }
    },
    addNewThread: (state, action) => {
      ensureMaps(state);
      const { thread } = action.payload;
      const threadIdStr = String(thread.threadId);

      const alreadyExists = state.threads.some(
        (t) => String(t.threadId) === threadIdStr
      );
      if (alreadyExists) return;

      const normalizedThread = {
        ...thread,
        lastMessageUpdate: thread.lastMessageUpdate ?? new Date().toISOString(),
      };

      state.threads = [normalizedThread, ...state.threads];

      state.allChatsLastMessage[threadIdStr] =
        normalizedThread.lastMessage ?? "";
      state.allChatsLastActivity[threadIdStr] =
        normalizedThread.lastActivity ?? normalizedThread.lastMessage ?? "";
      state.allChatsLastActivityType[threadIdStr] =
        normalizedThread.lastActivityType ?? "message";
      state.allChatsLastMessageTime[threadIdStr] =
        normalizedThread.lastMessageUpdate;
      state.allChatsUnreadCount[threadIdStr] =
        normalizedThread.unreadCount ?? 1;
      state.allChatsReadStatus[threadIdStr] = false;
    },
    groupLeftAction: (state, action) => {
      const { groupId } = action.payload;
      const key = String(groupId);

      state.threads = state.threads.filter((t) => String(t.threadId) !== key);

      delete state.allChatMessages[key];
      delete state.allChatsUnreadCount[key];
      delete state.allChatsLastMessage[key];
      delete state.allChatsLastActivity[key];
      delete state.allChatsLastActivityType[key];
      delete state.allChatsLastMessageTime[key];
      delete state.allChatsReadStatus[key];
      delete state.allChatsReactorId?.[key];
      delete state.chatPagination?.[key];
      if (state.pinnedMessages) delete state.pinnedMessages[key];

      if (String(state.currentThread?.threadId) === key) {
        state.currentThread = initialState.currentThread;
        state.currentThreadMessage = [];
      }
    },

    // Fired when someone ELSE leaves a group you're still in — just sync participants
    updateThreadParticipants: (state, action) => {
      const { groupId, updatedParticipants } = action.payload;
      const key = String(groupId);

      const threadIdx = state.threads.findIndex(
        (t) => String(t.threadId) === key
      );
      if (threadIdx !== -1) {
        state.threads[threadIdx] = {
          ...state.threads[threadIdx],
          participants: updatedParticipants,
        };
      }

      if (String(state.currentThread?.threadId) === key) {
        state.currentThread = {
          ...state.currentThread,
          participants: updatedParticipants,
        };
      }
    },
  },

  extraReducers: (builder) => {
    // ── getThreads ──────────────────────────────────────────────────────────
    builder.addCase(getThreads.pending, (state) => {
      state.loading = true;
      state.status = "pending";
    });
    builder.addCase(getThreads.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.status = "fulfilled";
      state.loading = false;
      state.error = null;

      if (!payload?.result?.length) {
        console.warn("[getThreads] Empty result — keeping existing threads");
        return;
      }
      // ── TEMP DEBUG: verify dedup hypothesis before making any fix ──────
      console.log(
        "[getThreads] raw result:",
        payload.result.map((t: any) => ({
          isGroup: t.isGroup,
          threadId: t.threadId,
          userid: t.userid,
          userId: t.userId,
          name: t.name,
          firstname: t.firstname,
          lastname: t.lastname,
        }))
      );

      const uniqueThreads = payload.result.reduce(
        (unique: any[], thread: any) => {
          const isDup = !!unique.find(
            (t) => String(t.threadId) === String(thread.threadId)
          );
          if (isDup) {
            console.log("[getThreads] DROPPED as duplicate:", {
              isGroup: thread.isGroup,
              threadId: thread.threadId,
              userid: thread.userid,
              userId: thread.userId,
              name: thread.name,
            });
          } else {
            unique.push(thread);
          }
          return unique;
        },
        []
      );

      console.log(
        "[getThreads] after dedup:",
        uniqueThreads.map((t: any) => ({
          isGroup: t.isGroup,
          threadId: t.threadId,
          userid: t.userid,
          userId: t.userId,
        }))
      );
      // ── END TEMP DEBUG ──────────────────────────────────────────────
      state.threads = uniqueThreads.map((t: any) => ({
        ...t,
        threadName: t.threadName ?? t.name,
        lastMessageUpdate:
          t.lastMessageTime ?? t.lastMessageUpdate ?? t.createdAt,
        messages: t.messages || [],
        unreadCount: t.unreadCount || 0,
        lastname:
          t.isGroup === false && +payload.userId === +t.userId
            ? `${t.lastname} (You)`
            : t.lastname,
      }));

      state.directMessageUsers = [];
      const existingCounts = state.allChatsUnreadCount || {};
      uniqueThreads.forEach((thread: any) => {
        const id = String(thread.threadId);
        const serverCount = +thread.unreadCount || 0;
        const socketCount = existingCounts[id] || 0;
        state.allChatsUnreadCount[id] = Math.max(serverCount, socketCount);
        state.allChatsLastMessage[id] = thread.lastMessage ?? "";
        state.allChatsLastActivity[id] =
          thread.lastActivity ?? thread.lastMessage ?? "";
        state.allChatsLastActivityType[id] =
          thread.lastActivityType ?? "message";
        state.allChatsLastMessageTime[id] =
          thread.lastMessageTime ??
          thread.lastMessageUpdate ??
          thread.createdAt;
        state.allChatsReactorId[id] = thread.reactorId ?? null;
        state.allChatsLastMessageMeta[id] = {
          senderId:
            thread.lastMessageSenderId != null
              ? String(thread.lastMessageSenderId)
              : null,
          readByAll: !!thread.lastMessageReadByAll,
          deliveredByAll: !!thread.lastMessageDeliveredByAll,
        };
      });
    });
    builder.addCase(getThreads.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getProjectsWithThreads ──────────────────────────────────────────────
    builder.addCase(getProjectsWithThreads.pending, (state) => {
      state.loading = true;
      state.status = "pending";
    });
    builder.addCase(getProjectsWithThreads.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = "fulfilled";
      state.projectsWithThreads = payload.result;
      state.error = null;
    });
    builder.addCase(getProjectsWithThreads.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
      state.projectsWithThreads = [];
    });

    // ── createThread ────────────────────────────────────────────────────────
    builder.addCase(createThread.pending, (state) => {
      state.loading = true;
      state.status = "pending";
    });
    builder.addCase(createThread.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
      state.error = null;
    });
    builder.addCase(createThread.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── updateThread ────────────────────────────────────────────────────────
    builder.addCase(updateThread.pending, (state) => {
      state.loading = true;
      state.status = "pending";
    });
    builder.addCase(updateThread.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = "fulfilled";
      if (payload.result[0]) {
        state.currentThread = { ...state.currentThread, ...payload.result[0] };
        const projectId = payload.result[0].projectId;
        if (projectId) {
          state.projectsWithThreads = state.projectsWithThreads.map(
            (project: any) => {
              if (project.threads.length > 0 && +project.id === +projectId) {
                const threads = project.threads.map((thread: any) =>
                  +thread.threadId === +payload.result[0].threadId
                    ? { ...thread, ...payload.result[0] }
                    : thread
                );
                return { ...project, threads };
              }
              return project;
            }
          );
        } else {
          state.threads = state.threads.map((thread) =>
            +thread.threadId === +payload.result[0].threadId
              ? { ...thread, ...payload.result[0] }
              : thread
          );
        }
      }
      state.error = null;
    });
    builder.addCase(updateThread.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getThreadsMembers ───────────────────────────────────────────────────
    // ── getThreadsMembers ───────────────────────────────────────────────────
    builder.addCase(getThreadsMembers.pending, (state) => {
      state.loading = true;
      state.status = "pending";
      state.threadMembers = []; // clear stale members from previous thread while loading
    });
    builder.addCase(getThreadsMembers.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.status = "fulfilled";
      state.error = null;
      state.threadMembers = Array.isArray(payload)
        ? payload
        : payload?.result ?? [];
    });
    builder.addCase(getThreadsMembers.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
      state.threadMembers = [];
    });

    // ── leaveThread ─────────────────────────────────────────────────────────
    builder.addCase(leaveThread.pending, (state) => {
      state.loading = true;
      state.status = "pending";
    });
    builder.addCase(leaveThread.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
      state.error = null;
    });
    builder.addCase(leaveThread.rejected, (state, { payload }) => {
      state.loading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getThreadMessages ───────────────────────────────────────────────────
    builder.addCase(getThreadMessages.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getThreadMessages.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      state.currentThreadMessage = payload.result;

      // Also seed the new cache so socket handlers can find messages
      if (payload.result.length > 0) {
        const chatIdStr = String(payload.result[0].threadId);
        state.allChatMessages[chatIdStr] = payload.result;
      }

      state.threads = state.threads.map((thread) => {
        if (+thread.threadId === +payload.result[0]?.threadId) {
          state.unreadCount = +thread.unreadCount;
          return { ...thread, unreadCount: 0, messages: [...payload.result] };
        }
        return thread;
      });
      state.projectsWithThreads = state.projectsWithThreads.map(
        (project: any) => {
          if (project.id === state.currentThread.projectId) {
            const newThreads = project.threads.map((thread: any) => {
              if (+thread.threadId === +payload.result[0]?.threadId) {
                state.unreadCount = +thread.unreadCount;
                return {
                  ...thread,
                  unreadCount: 0,
                  messages: [...payload.result],
                };
              }
              return thread;
            });
            return { ...project, threads: newThreads };
          }
          return project;
        }
      );
    });
    builder.addCase(getThreadMessages.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getMoreThreadMessages ───────────────────────────────────────────────
    builder.addCase(getMoreThreadMessages.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getMoreThreadMessages.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      state.currentThreadMessage = [
        ...payload.result,
        ...state.currentThreadMessage,
      ];

      // Prepend to new cache as well
      if (payload.result.length > 0) {
        const chatIdStr = String(payload.result[0].threadId);
        const existing = state.allChatMessages[chatIdStr] || [];
        const existingIds = new Set(existing.map((m) => String(m._id)));
        const newMsgs = payload.result.filter(
          (m: any) => !existingIds.has(String(m._id))
        );
        state.allChatMessages[chatIdStr] = [...newMsgs, ...existing];
      }

      state.threads = state.threads.map((thread) => {
        if (+thread.threadId === +payload.result[0]?.threadId) {
          state.unreadCount = +thread.unreadCount;
          return { ...thread, unreadCount: 0, messages: [...payload.result] };
        }
        return thread;
      });
    });
    builder.addCase(getMoreThreadMessages.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    builder.addCase(getThreadChat.pending, (state, { meta }) => {
      ensureMaps(state);
      state.getMessageLoading = true;
      state.status = "pending";
      const chatIdStr = String(meta.arg.chatId);
      state.chatPagination[chatIdStr] = {
        ...(state.chatPagination[chatIdStr] ?? {
          hasMore: true,
          oldestMessageDate: null,
        }),
        loading: true,
      };
    });

    builder.addCase(getThreadChat.fulfilled, (state, { payload, meta }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;

      const isInitialLoad = !meta.arg.lastMessage;
      const chatIdStr = String(payload.id);
      const sorted = [...payload.result].reverse(); // oldest → newest

      if (isInitialLoad) {
        const existing = state.allChatMessages[chatIdStr] || [];
        const optimisticOnly = existing.filter((m) => m.local === true);
        state.allChatMessages[chatIdStr] = [...sorted, ...optimisticOnly];
        state.chatCacheMeta[chatIdStr] = {
          source: "server",
          lastServerFetch: Date.now(),
          messageCount: sorted.length,
        };
      } else {
        const existing = state.allChatMessages[chatIdStr] || [];
        const existingIds = new Set(existing.map((m) => String(m._id)));
        const newMessages = sorted.filter(
          (m) => !existingIds.has(String(m._id))
        );
        state.allChatMessages[chatIdStr] = [...newMessages, ...existing];
        state.chatCacheMeta[chatIdStr] = {
          source: "server",
          lastServerFetch: Date.now(),
          messageCount: state.allChatMessages[chatIdStr].length,
        };
      }

      state.chatPagination[chatIdStr] = {
        hasMore: payload.hasMore,
        oldestMessageDate: payload.oldestCreatedAt ?? null,
        loading: false,
      };
    });

    builder.addCase(getThreadChat.rejected, (state, { meta }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      const chatIdStr = String(meta.arg.chatId);
      if (state.chatPagination[chatIdStr]) {
        state.chatPagination[chatIdStr].loading = false;
      }
    });

    // ── starMessageAPI ──────────────────────────────────────────────────────
    builder.addCase(starMessageAPI.pending, (state) => {
      state.getMessageLoading = true;
    });
    builder.addCase(starMessageAPI.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      const result = payload?.result ?? payload;
      const { chatId, messageId, userId } = result ?? {};
      if (!chatId || !messageId) return;
      const messages = state.allChatMessages[String(chatId)];
      const msg = messages?.find((m) => m._id === messageId);
      if (msg) {
        if (!Array.isArray(msg.starred)) msg.starred = [];
        if (!msg.starred.includes(String(userId)))
          msg.starred.push(String(userId));
      }
    });
    builder.addCase(starMessageAPI.rejected, (state) => {
      state.getMessageLoading = false;
    });

    // ── unStarMessageAPI ─────────────────────────────────────────────────────
    builder.addCase(unStarMessageAPI.pending, (state) => {
      state.getMessageLoading = true;
    });
    builder.addCase(unStarMessageAPI.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      const result = payload?.result ?? payload;
      const { chatId, messageId, userId } = result ?? {};
      if (!chatId || !messageId) return;
      const messages = state.allChatMessages[String(chatId)];
      const msg = messages?.find((m) => m._id === messageId);
      if (msg?.starred) {
        msg.starred = msg.starred.filter((id: string) => id !== String(userId));
      }
    });
    builder.addCase(unStarMessageAPI.rejected, (state) => {
      state.getMessageLoading = false;
    });

    // ── getStarredMessagesAPI ────────────────────────────────────────────────
    builder.addCase(getStarredMessagesAPI.pending, (state) => {
      state.starredMessagesLoading = true;
    });
    builder.addCase(getStarredMessagesAPI.fulfilled, (state, { payload }) => {
      state.starredMessagesLoading = false;
      const sorted = [...(payload.result ?? [])].sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      state.starredMessages = sorted;
    });
    builder.addCase(getStarredMessagesAPI.rejected, (state) => {
      state.starredMessagesLoading = false;
    });
    // ── getProjectUsers ─────────────────────────────────────────────────────
    // NOTE: this populates a *separate* project-scoped people picker list,
    // not the sidebar. Left untouched — unrelated to the thread unification.
    builder.addCase(getProjectUsers.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getProjectUsers.fulfilled, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      state.directMessageUsers = payload.result.map((user: any) => ({
        ...user,
        messages: [],
        unreadCount: 0,
        lastname:
          +payload.userId === +user.userId
            ? `${user.lastname} (You)`
            : user.lastname,
      }));
    });
    builder.addCase(getProjectUsers.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── forwardMessageToUsers ───────────────────────────────────────────────
    builder.addCase(forwardMessageToUsers.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(forwardMessageToUsers.fulfilled, (state) => {
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
    });
    builder.addCase(forwardMessageToUsers.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getUnreadCountDm ────────────────────────────────────────────────────
    // Now patches `threads` directly so DM unread counts loaded from this
    // endpoint show up in the same unified sidebar list.
    builder.addCase(getUnreadCountDm.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getUnreadCountDm.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      if (payload.user !== null && typeof payload.user !== "undefined") {
        state.threads = state.threads.map((thread) => {
          if (thread.isGroup) return thread;
          const uid = thread.userId ?? thread.userid;
          const count = payload.user.unreadUsers[+uid];
          return { ...thread, unreadCount: count ? count : 0 };
        });
      }
    });
    builder.addCase(getUnreadCountDm.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getPrivateMessages ──────────────────────────────────────────────────
    builder.addCase(getPrivateMessages.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getPrivateMessages.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      state.currentThreadMessage = payload.result;
      state.threads = state.threads.map((thread) => {
        const uid = thread.userId ?? thread.userid;
        if (+uid === +payload.reciverId) {
          state.unreadCount = +thread.unreadCount;
          return { ...thread, messages: payload.result, unreadCount: 0 };
        }
        return thread;
      });
    });
    builder.addCase(getPrivateMessages.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });

    // ── getMorePrivateMessages ──────────────────────────────────────────────
    builder.addCase(getMorePrivateMessages.pending, (state) => {
      state.getMessageLoading = true;
      state.status = "pending";
    });
    builder.addCase(getMorePrivateMessages.fulfilled, (state, { payload }) => {
      ensureMaps(state);
      state.getMessageLoading = false;
      state.status = "fulfilled";
      state.error = null;
      state.currentThreadMessage = [
        ...payload.result,
        ...state.currentThreadMessage,
      ];
      state.threads = state.threads.map((thread) => {
        const uid = thread.userId ?? thread.userid;
        if (+uid === +payload.reciverId) {
          state.unreadCount = +thread.unreadCount;
          return {
            ...thread,
            messages: state.currentThreadMessage,
            unreadCount: 0,
          };
        }
        return thread;
      });
    });
    builder.addCase(getMorePrivateMessages.rejected, (state, { payload }) => {
      state.getMessageLoading = false;
      state.status = "rejected";
      state.error = payload;
    });
  },
});

export const {
  resetToInitialConversation,
  updateConversations,
  updateConversationsMultiple,
  resetConversations,
  updateThreadType,
  updatePrivateConversations,
  updateThreadMessage,
  updateUnreadCountForProjectThreads,
  muteThread,
  updateReaction,
  updateThreadForUser,
  updateUnreadCountForMarkAsUnread,
  replyToRequest,
  closeRequest,
  addDummyDataForMessagesScreen,
  addMessage,
  updateAllChatUnreadCount,
  deleteLocalMessage,
  evictChatMessages,
  updateAllChatLastMessage,
  updateLastMessageTime,
  updateAllChatReadStatus,
  updateThreadLastActivity,
  updateMessageReceived,
  updateMessageRead,
  updateGroupInfo,
  groupDeletedAction,
  addNewThread,
  removeAttachmentFromMessage,
  updateMessageContent,
  starMessageLocally,
  removeStarredMessage,
  clearStarredMessages,
  setPinnedMessage,
  setThreadMedia,
  setCommonThreads,
  clearPinnedMessage,
  groupLeftAction,
  updateThreadParticipants,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
